import React from 'react';
import { 
  ExclamationTriangleIcon,
  UserPlusIcon,
  ArrowTrendingUpIcon,
  EllipsisHorizontalIcon,
  ChatBubbleLeftRightIcon,
  TicketIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

export default function AdminDashboard() {
  return (
    <div className="px-8 py-8 max-w-[1400px] mx-auto flex flex-col gap-8 pb-20">
      
      {/* Alert Banner */}
      <div className="bg-red-50 border border-red-200 rounded-[20px] p-4 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-red-100 text-red-600 rounded-xl flex items-center justify-center shrink-0">
            <ExclamationTriangleIcon className="w-6 h-6" />
          </div>
          <div>
            <h4 className="text-[15px] font-extrabold text-red-900 mb-0.5">Low API Credit Alert</h4>
            <p className="text-[13px] font-medium text-red-700">Serper API credits are below 15%. Your LLM routing may be affected in 48 hours.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button className="bg-red-800 text-white px-5 py-2.5 rounded-xl text-[13px] font-bold hover:bg-red-900 transition-colors shadow-sm">
            Recharge Now
          </button>
          <button className="bg-red-100 text-red-800 px-5 py-2.5 rounded-xl text-[13px] font-bold hover:bg-red-200 transition-colors">
            Dismiss
          </button>
        </div>
      </div>

      {/* Top Metrics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Total Users */}
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-[24px] p-8 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-8">
            <div className="w-12 h-12 bg-brand-50 text-brand-600 rounded-2xl flex items-center justify-center">
              <UserPlusIcon className="w-6 h-6" />
            </div>
            <span className="px-3 py-1.5 bg-emerald-50 text-emerald-600 text-[12px] font-extrabold rounded-full">
              +12.4%
            </span>
          </div>
          <div>
            <p className="text-[13px] font-bold text-slate-500 mb-1">Total Users</p>
            <h2 className="text-[38px] font-extrabold text-slate-900 tracking-tight leading-none mb-4">42,891</h2>
            <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="w-[70%] h-full bg-brand-500 rounded-full"></div>
            </div>
          </div>
        </div>

        {/* Platform Revenue */}
        <div className="lg:col-span-6 bg-white border border-slate-200 rounded-[24px] p-8 shadow-sm flex flex-col justify-between">
          <div className="flex items-start justify-between mb-6">
            <div>
              <p className="text-[13px] font-bold text-slate-500 mb-1">Platform Revenue (MTD)</p>
              <h2 className="text-[38px] font-extrabold text-slate-900 tracking-tight leading-none">$892,400.00</h2>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-extrabold text-brand-600 uppercase tracking-widest mb-1">Growth Velocity</p>
              <span className="text-[24px] font-extrabold text-slate-900">8.2%</span>
            </div>
          </div>
          
          {/* Mock Bar Chart */}
          <div className="flex items-end justify-between h-[100px] gap-2">
            <div className="w-full bg-brand-100 h-[20%] rounded-t-sm hover:bg-brand-500 transition-colors"></div>
            <div className="w-full bg-brand-200 h-[30%] rounded-t-sm hover:bg-brand-500 transition-colors"></div>
            <div className="w-full bg-brand-200 h-[35%] rounded-t-sm hover:bg-brand-500 transition-colors"></div>
            <div className="w-full bg-brand-300 h-[45%] rounded-t-sm hover:bg-brand-500 transition-colors"></div>
            <div className="w-full bg-brand-400 h-[50%] rounded-t-sm hover:bg-brand-500 transition-colors"></div>
            <div className="w-full bg-brand-500 h-[65%] rounded-t-sm hover:bg-brand-600 transition-colors"></div>
            <div className="w-full bg-brand-600 h-[85%] rounded-t-sm hover:bg-brand-700 transition-colors"></div>
            <div className="w-full bg-brand-600 h-[100%] rounded-t-sm shadow-sm hover:bg-brand-700 transition-colors"></div>
          </div>
        </div>

        {/* Conversion */}
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-[24px] p-8 shadow-sm flex flex-col justify-between">
          <div>
            <p className="text-[13px] font-bold text-slate-500 mb-1">Conversion: Non-Subs</p>
            <h2 className="text-[38px] font-extrabold text-slate-900 tracking-tight leading-none mb-3">14.8%</h2>
            <p className="text-[12px] text-slate-400 font-medium leading-snug">6,340 free users reached limit this month.</p>
          </div>
          
          {/* Radial Circle */}
          <div className="self-end mt-4">
            <div className="relative w-20 h-20 flex items-center justify-center rounded-full border-[6px] border-slate-100">
              <svg className="absolute inset-0 w-full h-full transform -rotate-90">
                <circle cx="34" cy="34" r="34" className="stroke-current text-brand-600" strokeWidth="6" fill="none" strokeDasharray="214" strokeDashoffset="180" />
              </svg>
              <span className="text-[14px] font-extrabold text-slate-900">14%</span>
            </div>
          </div>
        </div>

      </div>

      {/* Middle Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* User Activity Monitor */}
        <div className="lg:col-span-8 bg-white border border-slate-200 rounded-[24px] shadow-sm flex flex-col overflow-hidden">
          <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-[15px] font-extrabold text-slate-900 tracking-widest uppercase">User Activity Monitor</h3>
            <div className="flex items-center gap-3 text-slate-400">
              <button className="hover:text-slate-900 transition-colors"><EllipsisHorizontalIcon className="w-5 h-5" /></button>
              <button className="hover:text-slate-900 transition-colors"><ArrowDownTrayIcon className="w-5 h-5" /></button>
            </div>
          </div>
          <table className="w-full text-left whitespace-nowrap">
            <thead>
              <tr className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest border-b border-slate-100">
                <th className="px-8 py-4">User</th>
                <th className="px-8 py-4">Usage</th>
                <th className="px-8 py-4">Tier</th>
                <th className="px-8 py-4">Recent Export</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <tr className="hover:bg-slate-50/50 transition-colors">
                <td className="px-8 py-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center text-sm">JD</div>
                  <div>
                    <p className="text-[14px] font-bold text-slate-900">Julian Das</p>
                    <p className="text-[11px] text-slate-400">julian@vortex.io</p>
                  </div>
                </td>
                <td className="px-8 py-5">
                  <div className="flex items-center justify-between mb-1.5 text-[11px] font-bold text-slate-700">
                    <span>8.2GB</span><span>82%</span>
                  </div>
                  <div className="w-24 h-[4px] bg-slate-100 rounded-full"><div className="w-[82%] h-full bg-brand-500 rounded-full"></div></div>
                </td>
                <td className="px-8 py-5">
                  <span className="px-3 py-1 bg-brand-50 text-brand-600 text-[10px] font-extrabold uppercase tracking-widest rounded-full">Enterprise</span>
                </td>
                <td className="px-8 py-5 text-[13px] font-medium text-slate-600">Audit_Report_Q3.pdf</td>
              </tr>
              
              <tr className="hover:bg-slate-50/50 transition-colors">
                <td className="px-8 py-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-600 font-bold flex items-center justify-center text-sm">SK</div>
                  <div>
                    <p className="text-[14px] font-bold text-slate-900">Sarah K.</p>
                    <p className="text-[11px] text-slate-400">sarah@design.co</p>
                  </div>
                </td>
                <td className="px-8 py-5">
                  <div className="flex items-center justify-between mb-1.5 text-[11px] font-bold text-slate-700">
                    <span>1.4GB</span><span>14%</span>
                  </div>
                  <div className="w-24 h-[4px] bg-slate-100 rounded-full"><div className="w-[14%] h-full bg-brand-500 rounded-full"></div></div>
                </td>
                <td className="px-8 py-5">
                  <span className="px-3 py-1 bg-slate-100 text-slate-600 text-[10px] font-extrabold uppercase tracking-widest rounded-full">Pro</span>
                </td>
                <td className="px-8 py-5 text-[13px] font-medium text-slate-600">Asset_Pack_v2.zip</td>
              </tr>

              <tr className="hover:bg-slate-50/50 transition-colors">
                <td className="px-8 py-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-600 font-bold flex items-center justify-center text-sm">ML</div>
                  <div>
                    <p className="text-[14px] font-bold text-slate-900">Marcus L.</p>
                    <p className="text-[11px] text-slate-400">m.l@freelance.net</p>
                  </div>
                </td>
                <td className="px-8 py-5">
                  <div className="flex items-center justify-between mb-1.5 text-[11px] font-bold text-red-600">
                    <span>9.8GB</span><span>98%</span>
                  </div>
                  <div className="w-24 h-[4px] bg-red-100 rounded-full"><div className="w-[98%] h-full bg-red-500 rounded-full"></div></div>
                </td>
                <td className="px-8 py-5">
                  <span className="px-3 py-1 bg-purple-50 text-purple-600 text-[10px] font-extrabold uppercase tracking-widest rounded-full">Standard</span>
                </td>
                <td className="px-8 py-5 text-[13px] font-medium text-slate-400 italic">No exports</td>
              </tr>
            </tbody>
          </table>
          <div className="px-8 py-5 border-t border-slate-100 flex justify-center bg-slate-50/50">
            <button className="text-[11px] font-extrabold text-brand-600 uppercase tracking-widest hover:text-brand-800 transition-colors">
              View All 42,891 Users
            </button>
          </div>
        </div>

        {/* Right Side Column */}
        <div className="lg:col-span-4 flex flex-col gap-6">
          
          {/* Forecasted Revenue */}
          <div className="bg-[#0f172a] rounded-[24px] p-8 text-white shadow-xl shadow-slate-900/10 relative overflow-hidden">
            <ArrowTrendingUpIcon className="absolute top-4 right-4 w-24 h-24 text-white/[0.05] pointer-events-none" strokeWidth={2} />
            <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">Forecasted Revenue (Q4)</p>
            <h2 className="text-[40px] font-extrabold tracking-tight leading-none mb-3">$3,420,000</h2>
            <p className="text-[13px] text-slate-300 font-medium leading-relaxed mb-6">
              Predictive modeling suggests +18% increase based on current sign-up velocity.
            </p>
            <button className="w-full bg-brand-600 hover:bg-brand-500 text-white py-3.5 rounded-xl text-[14px] font-bold transition-colors shadow-sm">
              View Detail Projection
            </button>
          </div>

          {/* Pricing Controls */}
          <div className="bg-white border border-slate-200 rounded-[24px] p-8 shadow-sm flex flex-col gap-4">
            <h3 className="text-[15px] font-extrabold text-slate-900 tracking-tight mb-2">Pricing Controls</h3>
            
            <div className="border border-slate-200 rounded-xl p-4 cursor-pointer hover:border-brand-300 transition-colors">
              <div className="flex justify-between items-center mb-1">
                <h4 className="font-bold text-slate-900 text-[14px]">Standard</h4>
                <span className="font-bold text-brand-600 text-[14px]">$29/mo</span>
              </div>
              <p className="text-[11px] text-slate-500 font-medium">Basic AI generation tools, 10GB storage.</p>
            </div>

            <div className="border border-slate-200 rounded-xl p-4 cursor-pointer hover:border-brand-300 transition-colors">
              <div className="flex justify-between items-center mb-1">
                <h4 className="font-bold text-slate-900 text-[14px]">Professional</h4>
                <span className="font-bold text-brand-600 text-[14px]">$79/mo</span>
              </div>
              <p className="text-[11px] text-slate-500 font-medium">Advanced routing, 100GB storage, team collaboration.</p>
            </div>

            <div className="border border-slate-200 rounded-xl p-4 cursor-pointer hover:border-brand-300 transition-colors">
              <div className="flex justify-between items-center mb-1">
                <h4 className="font-bold text-slate-900 text-[14px]">Enterprise</h4>
                <span className="font-bold text-brand-600 text-[14px]">Custom</span>
              </div>
              <p className="text-[11px] text-slate-500 font-medium">Unlimited bandwidth, custom LLM fine-tuning.</p>
            </div>

            <button className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 py-3 rounded-xl text-[13px] font-bold transition-colors shadow-sm mt-2">
              Adjust Global Pricing
            </button>
          </div>

        </div>
      </div>

      {/* Bottom Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Critical Support Tickets */}
        <div className="bg-white border border-slate-200 rounded-[24px] p-8 shadow-sm flex flex-col">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-[17px] font-extrabold text-slate-900 tracking-tight">Critical Support Tickets</h3>
            <span className="px-3 py-1 bg-red-800 text-white text-[11px] font-extrabold rounded-full">4 Active</span>
          </div>

          <div className="flex flex-col gap-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-red-50 text-red-500 rounded-xl flex items-center justify-center shrink-0">
                <TicketIcon className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-start mb-1">
                  <h4 className="text-[14px] font-bold text-slate-900">API Timeout - Vertex Integration</h4>
                  <span className="text-[11px] text-slate-400 font-medium">12m ago</span>
                </div>
                <p className="text-[12px] text-slate-500 leading-snug mb-3">Enterprise client reported 504 errors on custom endpoints.</p>
                <div className="flex items-center gap-3 text-[12px] font-bold">
                  <button className="text-brand-600 hover:text-brand-800">Assign Tech</button>
                  <button className="text-red-600 hover:text-red-800">Escalate</button>
                </div>
              </div>
            </div>

            <div className="w-full h-px bg-slate-100"></div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-50 text-amber-500 rounded-xl flex items-center justify-center shrink-0">
                <ExclamationTriangleIcon className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-start mb-1">
                  <h4 className="text-[14px] font-bold text-slate-900">Payment Failure - Stripe Webhook</h4>
                  <span className="text-[11px] text-slate-400 font-medium">1h ago</span>
                </div>
                <p className="text-[12px] text-slate-500 leading-snug mb-3">System failed to capture $4.2k payment for User #8821.</p>
                <div className="flex items-center gap-3 text-[12px] font-bold">
                  <button className="text-brand-600 hover:text-brand-800">Retry Webhook</button>
                  <button className="text-slate-500 hover:text-slate-800">View Logs</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* High-Value Enquiries */}
        <div className="bg-white border border-slate-200 rounded-[24px] p-8 shadow-sm flex flex-col">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-[17px] font-extrabold text-slate-900 tracking-tight">High-Value Enquiries</h3>
            <button className="text-slate-400 hover:text-slate-900"><EllipsisHorizontalIcon className="w-6 h-6" /></button>
          </div>

          <div className="flex flex-col gap-6">
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-slate-200">
                    <img src="https://i.pravatar.cc/150?img=33" alt="Avatar" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <h4 className="text-[14px] font-bold text-slate-900 leading-none mb-1">Michael Thorne</h4>
                    <p className="text-[11px] text-slate-500 font-medium leading-none">CTO, Nexus Corp</p>
                  </div>
                </div>
                <span className="px-2.5 py-1 bg-emerald-50 text-emerald-600 text-[10px] font-extrabold uppercase tracking-widest rounded-md">New Lead</span>
              </div>
              <p className="text-[13px] text-slate-600 leading-relaxed font-medium mb-3 italic">
                "Looking to migrate 400 seats from competitor. Need custom LLM security clearance."
              </p>
              <div className="flex items-center gap-4 text-[12px] font-bold">
                <button className="flex items-center gap-1 text-slate-500 hover:text-slate-900"><ChatBubbleLeftRightIcon className="w-4 h-4" /> Schedule Demo</button>
                <span className="text-slate-400 font-medium">$ Est. $12k MRR</span>
              </div>
            </div>

            <div className="w-full h-px bg-slate-100"></div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-slate-200">
                    <img src="https://i.pravatar.cc/150?img=44" alt="Avatar" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <h4 className="text-[14px] font-bold text-slate-900 leading-none mb-1">Evelyn Wu</h4>
                    <p className="text-[11px] text-slate-500 font-medium leading-none">Procurement, Horizon</p>
                  </div>
                </div>
                <span className="px-2.5 py-1 bg-slate-100 text-slate-500 text-[10px] font-extrabold uppercase tracking-widest rounded-md">Pending Audit</span>
              </div>
              <p className="text-[13px] text-slate-600 leading-relaxed font-medium mb-3 italic">
                Requires SOC2 compliance documentation before proceeding with annual plan.
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}